import axios from "axios";
import { adminApiUrl, publicApiUrl, signedInApiUrl } from "../util/Constants";
import { isAdmin } from "../util/SecurityUtils";
import { setPlayerElo } from "../util/HelperUtils";
import type { Player, Tournament, TournamentDetail, PagedResponse, PlayerAwardsContext } from "../types/domain";

/**
 * Tournament detail returns players + matches + scores with each player nested.
 * Each player has a `ladders` array; we set `player.elo` to the elo for the
 * tournament's ladder so components can read `.elo` directly.
 */
const enrichWithLadderElo = (tournament: TournamentDetail): TournamentDetail => {
  const ladderId = tournament.ladder.id;
  tournament.players.forEach((seeding) => setPlayerElo(seeding.player, ladderId));
  tournament.rounds.forEach((round) => {
    round.scores.forEach((score) => setPlayerElo(score.player, ladderId));
    round.matches.forEach((match) => {
      setPlayerElo(match.playerOne, ladderId);
      setPlayerElo(match.playerTwo, ladderId);
    });
  });
  tournament.finalPlacements.forEach((p) => setPlayerElo(p.player, ladderId));
  return tournament;
};

interface ListPagedParams {
  page?: number;
  size?: number;
  ladder?: string | null;
}

export const tournamentsApi = {
  getById: async (id: number | string): Promise<TournamentDetail> => {
    const { data } = await axios.get<TournamentDetail>(`${publicApiUrl}/tournaments/${id}`);
    return enrichWithLadderElo(data);
  },

  getAwardsContext: async (id: number | string): Promise<PlayerAwardsContext[]> => {
    const { data } = await axios.get<PlayerAwardsContext[]>(`${publicApiUrl}/tournaments/${id}/awards-context`);
    return data;
  },

  list: async (): Promise<Tournament[]> => {
    const { data } = await axios.get<Tournament[]>(`${publicApiUrl}/tournaments`);
    return data;
  },

  listPaged: async ({ page = 0, size = 10, ladder = null }: ListPagedParams = {}): Promise<PagedResponse<Tournament>> => {
    const baseUrl = isAdmin() ? adminApiUrl : publicApiUrl;
    let endpoint = `${baseUrl}/tournaments/paged?page=${page}&size=${size}`;
    if (ladder) endpoint += `&ladder=${ladder}`;
    const { data } = await axios.get<PagedResponse<Tournament>>(endpoint);
    return data;
  },

  create: async (tournament: Partial<Tournament>): Promise<Tournament> => {
    const { data } = await axios.post<Tournament>(`${signedInApiUrl}/tournaments`, tournament);
    return data;
  },

  setPlayers: async ({ tournamentId, playerIds }: { tournamentId: number; playerIds: number[] }): Promise<unknown> => {
    const { data } = await axios.post(`${signedInApiUrl}/tournaments/${tournamentId}/players`, playerIds);
    return data;
  },

  start: async ({ tournamentId, numberOfGroups }: { tournamentId: number; numberOfGroups: number | null }): Promise<TournamentDetail> => {
    let url = `${signedInApiUrl}/tournaments/${tournamentId}/start`;
    if (numberOfGroups) url += `?numberOfGroups=${numberOfGroups}`;
    const { data } = await axios.post<TournamentDetail>(url);
    return data;
  },

  getBlastQueue: async (tournamentId: number): Promise<Player[]> => {
    const { data } = await axios.get<Player[]>(`${publicApiUrl}/tournaments/${tournamentId}/blast/queue`);
    return data;
  },

  blastBenchPlayer: async ({ tournamentId, playerId }: { tournamentId: number; playerId: number }): Promise<unknown> => {
    const { data } = await axios.post(`${signedInApiUrl}/tournaments/${tournamentId}/blast/players/${playerId}/bench`);
    return data;
  },

  blastReturnPlayer: async ({ tournamentId, playerId }: { tournamentId: number; playerId: number }): Promise<unknown> => {
    const { data } = await axios.post(`${signedInApiUrl}/tournaments/${tournamentId}/blast/players/${playerId}/return`);
    return data;
  },

  setPlayerSecondRoundGroup: async ({ tournamentId, playerId, group }: { tournamentId: number; playerId: number; group: number }): Promise<unknown> => {
    const { data } = await axios.post(`${signedInApiUrl}/tournaments/${tournamentId}/players/${playerId}/group?group=${group}`);
    return data;
  },

  finishRound: async (tournamentId: number): Promise<unknown> => {
    const { data } = await axios.post(`${signedInApiUrl}/tournaments/${tournamentId}/round`);
    return data;
  },

  cancelRound: async (tournamentId: number): Promise<unknown> => {
    const { data } = await axios.delete(`${signedInApiUrl}/tournaments/${tournamentId}/round`);
    return data;
  },

  finishTournament: async (tournamentId: number): Promise<unknown> => {
    const { data } = await axios.post(`${signedInApiUrl}/tournaments/${tournamentId}/finish`);
    return data;
  },

  setDoubleElimination: async ({ tournamentId, enabled }: { tournamentId: number; enabled: boolean }): Promise<unknown> => {
    const { data } = await axios.post(`${signedInApiUrl}/tournaments/${tournamentId}/double-elimination?enabled=${enabled}`);
    return data;
  },

  toggleBlastGeneration: async ({ tournamentId, isStopped }: { tournamentId: number; isStopped: boolean }): Promise<unknown> => {
    const endpoint = isStopped ? "resume-generation" : "stop-generation";
    const { data } = await axios.post(`${signedInApiUrl}/tournaments/${tournamentId}/blast/${endpoint}`);
    return data;
  },
};
